import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";
import { GiDunceCap } from "react-icons/gi";
import { POST } from "../../service/CRUDService";
import AppConstants from "../../configs/AppConstants";
import EntityConstants from "../../configs/EntityConstants";
import { useTranslation } from "react-i18next";

export default function ForgotPasswordModal({
  isModalOpen,
  toggleModal,
  certificate_name,
  certificate_header,
  certificate_description,
}) {
  const { t } = useTranslation();
  const InputBorderRadius = "0.5rem";
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [captchaValue, setCaptchaValue] = useState(null);

  function closeModal() {
    toggleModal(false);
  }

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const emailInput = e.target.value.trim();
    setEmail(emailInput);
    setIsEmailValid(validateEmail(emailInput));
  };

  const forgotPassword = async () => {
    if (!isEmailValid) {
      toast.error(t("homeAuthPages:valid_email_required"));
      return;
    }
    try {
      const response = await POST(EntityConstants.POST_FORGOT_PASSWORD, {
        email,
      });
      const data = await response.json();
      toast.success(data.message);
      closeModal();
    } catch (error) {
      console.error("Failed to send email", error);
      let errorMessage = t("homeAuthPages:email_send_error");

      if (error?.response) {
        try {
          const errorData = await error.response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }
      }

      toast.error(errorMessage);
    }
  };

  return (
    <>
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Dialog.Overlay className="fixed inset-0 bg-black/25 backdrop-blur-md" />
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel id="forgot-password-modal" className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <button
                    id="forgot-password-modal-close-button"
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex justify-center"
                  >
                    {certificate_header}
                  </Dialog.Title>
                  <div className="flex flex-col gap-2 mt-2">
                    <div>
                      <input
                        type="email"
                        name="email"
                        id="forgot-password-email-input"
                        required
                        placeholder={"Enter your email"}
                        autoComplete="true"
                        className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                        style={{ borderRadius: InputBorderRadius }}
                        value={email}
                        onChange={handleEmailChange}
                      />
                      {!isEmailValid && (
                        <p id="forgot-password-email-error-description" className="text-red-500 text-sm mt-1">
                          {t("homeAuthPages:valid_email_required")}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-center text-sm text-gray-500">
                        {certificate_description}
                      </p>
                    </div>

                    <div className="flex justify-center mt-4">
                      <ReCAPTCHA
                        sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                        onChange={handleCaptchaChange}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      id="forgot-password-submit-button"
                      type="button"
                      onClick={forgotPassword}
                      className="py-2.5 px-5 text-sm font-medium text-white bg-cfPrimary border border-gray-200 rounded-xl focus:outline-none"
                      disabled={!isEmailValid || !captchaValue}
                    >
                      {t("homeAuthPages:submit")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
